import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth } from "@/App";
import { cvApi } from "@/lib/cv";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import * as Icons from "lucide-react";
import {
  Mail, ShieldCheck, MessageSquare, DollarSign, BookOpen, ArrowLeft,
  Wand2, Loader2, Copy, CheckCircle, AlertTriangle, Sparkles, Award,
} from "lucide-react";
import { US_STATES } from "@/lib/listings";
import { toast } from "sonner";

const TABS = [
  { key: "cover-letter", ar: "خطاب التغطية",      en: "Cover Letter",   Icon: Mail },
  { key: "ats",          ar: "تحسين ATS",          en: "ATS Optimizer",   Icon: ShieldCheck },
  { key: "interview",    ar: "تحضير المقابلات",    en: "Interview Prep",  Icon: MessageSquare },
  { key: "salary",       ar: "دليل الرواتب",       en: "Salary Guide",    Icon: DollarSign },
  { key: "advice",       ar: "المسار المهني",      en: "Career Advice",   Icon: BookOpen },
];

export default function CVTools() {
  const { tool: tabKey } = useParams();
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [professions, setProfessions] = useState([]);

  const active = TABS.find(x => x.key === tabKey) || TABS[0];

  useEffect(() => { cvApi.professions().then(setProfessions).catch(() => {}); }, []);

  return (
    <DashboardLayout title={t("أدوات السيرة", "Resume Tools")}>
      <div className="space-y-6" data-testid="cv-tools">
        <div className="glass-card rounded-2xl p-3 flex items-center gap-2">
          <Button onClick={() => navigate("/cv")} variant="outline" size="sm" className="border-slate-700 text-slate-300">
            <ArrowLeft size={14} className="ml-1" />{t("مركز السيرة", "CV Center")}
          </Button>
          <div className="flex flex-wrap gap-2 mr-2">
            {TABS.map((tab) => (
              <button key={tab.key} onClick={() => navigate(`/cv/tools/${tab.key}`)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition border ${
                  active.key === tab.key ? "bg-yellow-500/15 border-yellow-500/60 text-yellow-300" : "bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-500"
                }`}
                data-testid={`tools-tab-${tab.key}`}>
                <tab.Icon size={12} />{language === "ar" ? tab.ar : tab.en}
              </button>
            ))}
          </div>
        </div>

        {active.key === "cover-letter" && <CoverLetter t={t} language={language} token={token} professions={professions} />}
        {active.key === "ats"          && <ATS         t={t} language={language} token={token} />}
        {active.key === "interview"    && <Interview   t={t} language={language} professions={professions} />}
        {active.key === "salary"       && <Salary      t={t} language={language} professions={professions} />}
        {active.key === "advice"       && <Advice      t={t} language={language} />}
      </div>
    </DashboardLayout>
  );
}

const inputCls = "bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white w-full text-sm focus:border-yellow-500 outline-none";

// ─────── Cover Letter ───────
function CoverLetter({ t, language, token, professions }) {
  const [form, setForm] = useState({ full_name: "", job_title: "", company: "", profession: "", experience_summary: "", job_description: "" });
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!token) { toast.error(t("سجّل دخولك", "Sign in first")); return; }
    if (!form.job_title || !form.company) { toast.error(t("املأ الوظيفة والشركة", "Fill role + company")); return; }
    setLoading(true);
    try {
      const r = await cvApi.coverLetter({ ...form, language: language === "ar" ? "ar" : "en" }, token);
      setOutput(r.letter);
    } catch { toast.error(t("فشل التوليد", "Generation failed")); } finally { setLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="glass-card rounded-2xl p-5 space-y-3">
        <h3 className="text-lg font-bold text-white flex items-center gap-2"><Mail className="text-purple-400" size={18} />{t("معلومات الخطاب", "Letter info")}</h3>
        <input placeholder={t("اسمك الكامل", "Full name")} value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} className={inputCls} />
        <div className="grid grid-cols-2 gap-2">
          <input placeholder={t("المسمى الوظيفي", "Job title")} value={form.job_title} onChange={e => setForm({...form, job_title: e.target.value})} className={inputCls} data-testid="cl-job-title" />
          <input placeholder={t("الشركة", "Company")} value={form.company} onChange={e => setForm({...form, company: e.target.value})} className={inputCls} data-testid="cl-company" />
        </div>
        <select value={form.profession} onChange={e => setForm({...form, profession: e.target.value})} className={inputCls}>
          <option value="">{t("اختر مهنتك", "Profession")}</option>
          {professions.map(p => <option key={p.slug} value={p.slug}>{language === "ar" ? p.ar : p.en}</option>)}
        </select>
        <textarea rows={3} placeholder={t("ملخص خبرتك (سطرين-ثلاثة)…", "Your experience summary (2-3 lines)…")}
          value={form.experience_summary} onChange={e => setForm({...form, experience_summary: e.target.value})} className={inputCls} />
        <textarea rows={4} placeholder={t("الصق وصف الوظيفة (اختياري)…", "Paste the job description (optional)…")}
          value={form.job_description} onChange={e => setForm({...form, job_description: e.target.value})} className={inputCls} />
        <Button onClick={generate} disabled={loading} className="btn-primary w-full" data-testid="cl-generate">
          {loading ? <Loader2 className="animate-spin ml-2" size={14} /> : <Wand2 className="ml-2" size={14} />}
          {t("ولّد الخطاب", "Generate letter")}
        </Button>
      </div>
      <div className="glass-card rounded-2xl p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-white">{t("الخطاب المولّد", "Generated letter")}</h3>
          {output && <Button size="sm" onClick={() => { navigator.clipboard.writeText(output); toast.success(t("تم النسخ","Copied")); }} className="btn-primary"><Copy size={12} className="ml-1" />{t("نسخ", "Copy")}</Button>}
        </div>
        {output ? <pre className="text-sm text-slate-200 whitespace-pre-wrap font-sans leading-relaxed" data-testid="cl-output">{output}</pre>
                : <p className="text-slate-500 text-sm">{t("املأ النموذج واضغط ولّد الخطاب.", "Fill the form and click Generate.")}</p>}
      </div>
    </div>
  );
}

// ─────── ATS ───────
function ATS({ t, language, token }) {
  const [resumeText, setResumeText] = useState("");
  const [jd, setJd] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const analyze = async () => {
    if (!token) { toast.error(t("سجّل دخولك", "Sign in first")); return; }
    if (resumeText.length < 100) { toast.error(t("الصق نص السيرة كاملاً", "Paste your full resume text")); return; }
    setLoading(true);
    try {
      const r = await cvApi.atsAnalyze({ resume_text: resumeText, job_description: jd, language: language === "ar" ? "ar" : "en" }, token);
      setResult(r);
    } catch { toast.error(t("فشل التحليل", "Analysis failed")); } finally { setLoading(false); }
  };

  const scoreColor = (s) => s >= 80 ? "text-emerald-400" : s >= 60 ? "text-yellow-400" : "text-red-400";

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="glass-card rounded-2xl p-5 space-y-3">
        <h3 className="text-lg font-bold text-white flex items-center gap-2"><ShieldCheck className="text-emerald-400" size={18} />{t("تحليل ATS", "ATS Analysis")}</h3>
        <textarea rows={10} placeholder={t("الصق نص سيرتك الذاتية…", "Paste your full resume text…")}
          value={resumeText} onChange={e => setResumeText(e.target.value)} className={inputCls} data-testid="ats-resume" />
        <textarea rows={6} placeholder={t("الصق وصف الوظيفة المستهدفة (اختياري لكن أفضل)…", "Paste the target job description (optional but recommended)…")}
          value={jd} onChange={e => setJd(e.target.value)} className={inputCls} data-testid="ats-jd" />
        <Button onClick={analyze} disabled={loading} className="btn-primary w-full" data-testid="ats-analyze">
          {loading ? <Loader2 className="animate-spin ml-2" size={14} /> : <ShieldCheck className="ml-2" size={14} />}
          {t("حلّل السيرة", "Analyze")}
        </Button>
      </div>
      <div className="glass-card rounded-2xl p-5 space-y-3">
        <h3 className="text-lg font-bold text-white">{t("النتيجة", "Result")}</h3>
        {!result ? <p className="text-slate-500 text-sm">{t("الصق سيرتك واضغط حلّل.", "Paste your resume and click Analyze.")}</p> : (
          <div className="space-y-4" data-testid="ats-result">
            <div className="text-center bg-slate-900/60 rounded-xl py-4">
              <p className="text-xs text-slate-500 mb-1">{t("درجة التوافق", "ATS Score")}</p>
              <p className={`text-5xl font-bold ${scoreColor(result.score)}`}>{result.score}/100</p>
            </div>
            <ResultBlock label={t("نقاط القوة","Strengths")} items={result.strengths} icon={<CheckCircle className="text-emerald-400" size={14} />} />
            <ResultBlock label={t("الثغرات","Gaps")} items={result.gaps} icon={<AlertTriangle className="text-amber-400" size={14} />} />
            <ResultBlock label={t("الكلمات المفقودة","Missing keywords")} items={result.missing_keywords} icon={<Sparkles className="text-yellow-400" size={14} />} />
            <ResultBlock label={t("الكلمات المتطابقة","Matched keywords")} items={result.keyword_matches} icon={<CheckCircle className="text-emerald-400" size={14} />} />
            <ResultBlock label={t("اقتراحات التحسين","Suggestions")} items={result.suggestions} icon={<Wand2 className="text-purple-400" size={14} />} />
          </div>
        )}
      </div>
    </div>
  );
}
const ResultBlock = ({ label, items, icon }) => items?.length ? (
  <div>
    <p className="text-sm font-semibold text-white mb-1 flex items-center gap-2">{icon}{label}</p>
    <ul className="space-y-1">{items.map((s, i) => <li key={i} className="text-sm text-slate-300">• {s}</li>)}</ul>
  </div>
) : null;

// ─────── Interview ───────
function Interview({ t, language, professions }) {
  const [profession, setProfession] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (slug) => {
    setLoading(true);
    try { setData(await cvApi.interview(slug || "general", language === "ar" ? "ar" : "en")); }
    catch { /* */ } finally { setLoading(false); }
  };

  useEffect(() => { load(profession); /* eslint-disable-next-line */ }, [profession]);

  return (
    <div className="glass-card rounded-2xl p-5 space-y-4" data-testid="interview-prep">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-lg font-bold text-white flex items-center gap-2"><MessageSquare className="text-blue-400" size={18} />{t("تحضير المقابلات","Interview Prep")}</h3>
        <select value={profession} onChange={e => setProfession(e.target.value)} className={inputCls + " max-w-xs"} data-testid="interview-profession">
          <option value="">{t("عام","General")}</option>
          {professions.map(p => <option key={p.slug} value={p.slug}>{language === "ar" ? p.ar : p.en}</option>)}
        </select>
      </div>
      {loading ? <Loader2 className="animate-spin text-yellow-500 mx-auto" /> : data && (
        <div className="space-y-5">
          {data.specific?.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-yellow-400 mb-2">{t(`أسئلة خاصة بـ ${data.profession_label}`,`Questions for ${data.profession_label}`)}</p>
              <QAList items={data.specific} t={t} />
            </div>
          )}
          <div>
            <p className="text-sm font-semibold text-slate-300 mb-2">{t("أسئلة عامة","General Questions")}</p>
            <QAList items={data.general} t={t} />
          </div>
        </div>
      )}
    </div>
  );
}
const QAList = ({ items, t }) => (
  <div className="space-y-3">
    {items.map((qa, i) => (
      <div key={i} className="bg-slate-900/60 rounded-xl p-3" data-testid={`qa-${i}`}>
        <p className="text-white font-semibold mb-1">{i+1}. {qa.q}</p>
        <p className="text-sm text-slate-300"><strong className="text-yellow-400">{t("نصيحة","Tip")}: </strong>{qa.a_tip}</p>
      </div>
    ))}
  </div>
);

// ─────── Salary ───────
function Salary({ t, language, professions }) {
  const [profession, setProfession] = useState("software_engineer");
  const [state, setState] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setData(await cvApi.salary(profession, state)); }
    catch { /* */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [profession, state]);
  const fmt = (n) => n ? `$${n.toLocaleString()}` : "—";

  return (
    <div className="glass-card rounded-2xl p-5 space-y-4" data-testid="salary-guide">
      <h3 className="text-lg font-bold text-white flex items-center gap-2"><DollarSign className="text-emerald-400" size={18} />{t("دليل الرواتب","Salary Guide")}</h3>
      <div className="grid grid-cols-2 gap-3">
        <select value={profession} onChange={e => setProfession(e.target.value)} className={inputCls} data-testid="salary-profession">
          {professions.map(p => <option key={p.slug} value={p.slug}>{language === "ar" ? p.ar : p.en}</option>)}
        </select>
        <select value={state} onChange={e => setState(e.target.value)} className={inputCls} data-testid="salary-state">
          <option value="">{t("متوسط أمريكا","US National")}</option>
          {US_STATES.map(s => <option key={s.code} value={s.code}>{language === "ar" ? s.name_ar : s.name_en}</option>)}
        </select>
      </div>
      {loading || !data ? <Loader2 className="animate-spin text-yellow-500 mx-auto" /> : (
        <div className="grid sm:grid-cols-3 gap-3" data-testid="salary-cards">
          <Stat label={t("الحد الأدنى","Low (25th)")} value={fmt(data.low)} color="text-slate-300" />
          <Stat label={t(`${data.state ? data.state + " " : ""}الوسيط`,`${data.state ? data.state + " " : ""}Median`)} value={fmt(data.state_median || data.national_median)} color="text-yellow-400" big />
          <Stat label={t("الحد الأعلى","High (top 10%)")} value={fmt(data.high)} color="text-emerald-400" />
          <div className="sm:col-span-3 text-xs text-slate-500 text-center">{data.source}</div>
        </div>
      )}
    </div>
  );
}
const Stat = ({ label, value, color, big }) => (
  <div className="bg-slate-900/60 rounded-xl p-4 text-center">
    <p className="text-xs text-slate-500 mb-1">{label}</p>
    <p className={`${big ? "text-3xl" : "text-2xl"} font-bold ${color}`}>{value}</p>
  </div>
);

// ─────── Career Advice ───────
function Advice({ t, language }) {
  const [data, setData] = useState(null);
  useEffect(() => { cvApi.careerAdvice().then(setData).catch(() => {}); }, []);

  if (!data) return <Loader2 className="animate-spin text-yellow-500 mx-auto" />;

  const renderBlock = (b, i) => {
    const Icon = Icons[b.icon] || Award;
    return (
      <div key={i} className="glass-card rounded-2xl p-5 space-y-2" data-testid={`advice-block-${i}`}>
        <h4 className="text-lg font-bold text-white flex items-center gap-2"><Icon className="text-yellow-500" size={18} />{language === "ar" ? b.title_ar : (b.title_en || b.title_ar)}</h4>
        <div className="space-y-2 text-sm text-slate-300">{b.body_ar.map((line, j) => <p key={j} dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>') }} />)}</div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2"><BookOpen className="text-yellow-500" size={18} />{t("النصائح المهنية","Career Advice")}</h3>
        <div className="grid lg:grid-cols-2 gap-4">{data.advice.map(renderBlock)}</div>
      </div>
      <div>
        <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2"><Sparkles className="text-purple-400" size={18} /> 🇺🇸 {t("نصائح للمهاجرين","Immigrant-Focused Tips")}</h3>
        <div className="grid lg:grid-cols-2 gap-4">{data.immigrant_tips.map(renderBlock)}</div>
      </div>
    </div>
  );
}
